/**
 * Filter Panel — deferred-apply multi-checkbox filtering.
 *
 * Opens/closes a slide-out panel triggered by the filter/sort button.
 * Checkboxes toggle locally without redirecting. On "Apply",
 * the script builds one final URL and redirects once.
 *
 * Data attributes used (namespaced to avoid editions.js conflicts):
 *   data-filter-panel            – filter/sort trigger element
 *   data-filter-panel-trigger    – the filter/sort trigger button
 *   data-filter-panel-dialog     – the dialog element
 *   data-filter-panel-overlay    – background overlay
 *   data-filter-panel-close      – close button inside panel
 *   data-filter-panel-group      – each filter group fieldset
 *   data-filter-panel-checkbox   – each checkbox input
 *   data-filter-panel-price-min  – price range min input
 *   data-filter-panel-price-max  – price range max input
 *   data-filter-panel-apply      – apply button
 *   data-filter-panel-clear      – clear button
 *   data-availability-param      – hidden availability filter param name
 */

(function () {
  'use strict';

  var trigger = document.querySelector('[data-filter-panel-trigger]');
  var dialog = document.querySelector('[data-filter-panel-dialog]');

  if (!trigger || !dialog) return;

  var overlay = dialog.querySelector('[data-filter-panel-overlay]');
  var closeBtn = dialog.querySelector('[data-filter-panel-close]');
  var applyBtn = dialog.querySelector('[data-filter-panel-apply]');
  var clearBtn = dialog.querySelector('[data-filter-panel-clear]');
  var currentSort = dialog.getAttribute('data-current-sort') || '';
  var availabilityParam = dialog.getAttribute('data-availability-param') || '';

  // -------------------------------------------------------
  // Focus trap
  // -------------------------------------------------------

  function getFocusableElements() {
    var els = dialog.querySelectorAll(
      'button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(els, function (el) {
      return !el.closest('[hidden]') && el.offsetParent !== null;
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusableElements();
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // -------------------------------------------------------
  // Open / Close
  // -------------------------------------------------------

  function open() {
    dialog.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    dialog.addEventListener('keydown', trapFocus);
    dialog.addEventListener('keydown', onEscape);

    var focusable = getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }

  function close() {
    dialog.setAttribute('hidden', '');
    trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    dialog.removeEventListener('keydown', trapFocus);
    dialog.removeEventListener('keydown', onEscape);
    trigger.focus();
  }

  function onEscape(e) {
    if (e.key === 'Escape') {
      close();
    }
  }

  // -------------------------------------------------------
  // Apply — build URL and redirect once
  // -------------------------------------------------------

  function buildApplyUrl() {
    var params = new URLSearchParams(window.location.search);

    // Remove page param so filters reset pagination
    params.delete('page');

    // Collect all filter groups in the panel
    var groups = dialog.querySelectorAll('[data-filter-panel-group]');

    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var paramName = group.getAttribute('data-param-name');

      if (paramName) {
        // List / boolean filter group
        // 1. Remove all existing params for this group
        params.delete(paramName);

        // 2. Append each checked value
        var checkboxes = group.querySelectorAll('[data-filter-panel-checkbox]');
        for (var j = 0; j < checkboxes.length; j++) {
          if (checkboxes[j].checked) {
            params.append(paramName, checkboxes[j].value);
          }
        }
      } else {
        // Price range filter group
        var paramMin = group.getAttribute('data-param-name-min');
        var paramMax = group.getAttribute('data-param-name-max');
        var minInput = group.querySelector('[data-filter-panel-price-min]');
        var maxInput = group.querySelector('[data-filter-panel-price-max]');

        if (paramMin) {
          params.delete(paramMin);
          if (minInput && minInput.value !== '') {
            params.append(paramMin, minInput.value);
          }
        }
        if (paramMax) {
          params.delete(paramMax);
          if (maxInput && maxInput.value !== '') {
            params.append(paramMax, maxInput.value);
          }
        }
      }
    }

    // Collect selected sort option
    var sortRadio = dialog.querySelector('[data-filter-panel-sort-radio]:checked');
    if (sortRadio) {
      params.set('sort_by', sortRadio.value);
    } else if (currentSort) {
      params.set('sort_by', currentSort);
    }

    // Keep hidden availability filtering active unless URL explicitly sets it.
    if (availabilityParam && !params.has(availabilityParam)) {
      params.set(availabilityParam, '1');
    }

    var queryString = params.toString();
    return window.location.pathname + (queryString ? '?' + queryString : '');
  }

  function apply() {
    var url = buildApplyUrl();
    window.location.href = url;
  }

  // -------------------------------------------------------
  // Clear — uncheck all checkboxes and clear price inputs
  // -------------------------------------------------------

  function clearAll() {
    var checkboxes = dialog.querySelectorAll('[data-filter-panel-checkbox]');
    for (var i = 0; i < checkboxes.length; i++) {
      checkboxes[i].checked = false;
    }

    var priceInputs = dialog.querySelectorAll(
      '[data-filter-panel-price-min], [data-filter-panel-price-max]'
    );
    for (var j = 0; j < priceInputs.length; j++) {
      priceInputs[j].value = '';
    }
  }

  // -------------------------------------------------------
  // Event listeners (never inline onclick)
  // -------------------------------------------------------

  trigger.addEventListener('click', open);

  if (overlay) {
    overlay.addEventListener('click', close);
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }

  if (applyBtn) {
    applyBtn.addEventListener('click', apply);
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', clearAll);
  }
})();
